import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { insights } from '@repletoryreach/sdk-api';
import { ActionIcon, Card, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useI18n } from '../../i18n/I18nProvider';
import { formatCredits } from '../../lib/credits';
import { formatCount, formatDateTime } from '../../lib/format';
import { INSIGHTS_KEY, insightErrorText } from './lib/data';
import { MUTED, ReportStatus, depthLabel, periodLabel } from './shared';

/** The business's reports, newest first: each opens its page; one not running can be deleted. */
export function ReportsList({ reports, loading }: { reports: insights.InsightReportSummary[]; loading: boolean }) {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: (id: string) => insights.deleteInsightReport(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: [INSIGHTS_KEY] }); notifySuccess(t('insights.list.deleted'), t('insights.title')); },
    onError: (e) => notifyError(insightErrorText(t, e), t('insights.title'))
  });

  if (!loading && reports.length === 0) {
    return <EmptyState title={t('insights.list.empty')} description={t('insights.list.emptyHint')} />;
  }
  return (
    <Stack gap="sm" data-testid="insights-reports">
      {reports.map((r) => (
        <Card key={r.id} withBorder className="rr-insights__report" data-testid="insights-report-row">
          <Group justify="space-between" align="flex-start" gap="sm" wrap="nowrap">
            <Link to={`/insights/${r.id}`} className="rr-insights__report-link">
              <Stack gap={4}>
                <Group gap="xs" wrap="wrap">
                  <Text role="bodySmall" fw={600}>{periodLabel(t, r.from, r.to, locale)}</Text>
                  <ReportStatus status={r.status} t={t} />
                  {r.automatic && <Text role="caption" c={MUTED}>{t('insights.list.monthly')}</Text>}
                </Group>
                {r.headline && <Text role="bodySmall">{r.headline}</Text>}
                {(r.status === 'failed' || r.status === 'skipped') && r.error && (
                  <Text role="caption" c={r.status === 'failed' ? 'var(--rr-color-error)' : MUTED}>{r.error}</Text>
                )}
                {r.kpis && (
                  <Text role="caption" c={MUTED}>
                    {[
                      t('insights.list.chats', { count: formatCount(r.kpis.chats) }),
                      r.kpis.answered_by_bot_pct != null ? t('insights.list.answered', { pct: r.kpis.answered_by_bot_pct }) : null,
                      t('insights.list.unanswered', { count: formatCount(r.kpis.unanswered) })
                    ].filter(Boolean).join(' · ')}
                  </Text>
                )}
                <Text role="caption" c={MUTED}>
                  {[depthLabel(t, r.depth), formatDateTime(r.created_at),
                    r.spent_minor > 0 ? t('insights.list.cost', { amount: formatCredits(r.spent_minor, t, r.currency) }) : null].filter(Boolean).join(' · ')}
                </Text>
              </Stack>
            </Link>
            {r.status !== 'queued' && r.status !== 'running' && (
              <ActionIcon variant="subtle" aria-label={t('insights.list.delete')} data-testid="insights-delete"
                onClick={() => openConfirmModal({
                  title: t('insights.list.deleteTitle'),
                  children: <Text role="bodySmall">{t('insights.list.deleteBody')}</Text>,
                  labels: { confirm: t('insights.list.delete'), cancel: t('action.cancel') },
                  onConfirm: () => remove.mutate(r.id)
                })}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" />
                </svg>
              </ActionIcon>
            )}
          </Group>
        </Card>
      ))}
    </Stack>
  );
}
